import PointerInteraction from 'ol/interaction/Pointer';
import { distance } from 'ol/coordinate';
import BaseEvent from 'ol/events/Event';

/** @type {number} The longpress duration. */
const DEFAULT_LONG_PRESS_DURATION = 700;

/** @type {number} The pixel tolerance of event. */
const DEFAULT_PIXEL_TOLERANCE = 10;

/**
 * @enum {string}
 */
export const LongPressEventType = {
  /**
     * Triggered when longpress start.
     * @event LongPressEvent#lpstart
     * @api
     */
  LONGPRESS_START: 'lpstart',
  /**
     * Triggered when longpress is well done.
     * @event LongPressEvent#lpdone
     * @api
     */
  LONGPRESS_DONE: 'lpdone',
  /**
     * Triggered when longpress interaction is cancelled..
     * @event LongPressEvent#lpcancel
     * @api
     */
  LONGPRESS_CANCEL: 'lpcancel',
};

export class LongPressEvent extends BaseEvent {
  /**
     * @constructor
     * @param {LongPressEventType} type The event type.
     * @param {import("ol/coordinate").Coordinate} coord The event coordinate
     */
  constructor(type, coord) {
    super(type);

    this.coord = coord;
  }
}

/**
 * @typedef {Object} LongPressOptions
 * @property {number} [duration] The long press duration. Default value is 700ms.
 * @property {number} [hitTolerence] The hit tolerence with in event can drag without cancel longpress event. Default value is 10px.
 */

/**
 * @classdesc
 * Interaction that adds long press interaction.
 * @fires LongPressEvent
 * @api
 */
export default class LongPress extends PointerInteraction {
  /**
     * @constructor
     * @param {LongPressOptions} options The long press options
     */
  constructor(options = {}) {
    super();

    /** @type {number} */
    this.duration_ = options.duration || DEFAULT_LONG_PRESS_DURATION;

    /** @type {number} */
    this.tolerence_ = options.hitTolerence || DEFAULT_PIXEL_TOLERANCE;

    /**
         * The Timer for longpress event.
         */
    this.timeout_ = null;

    /**
         * Pixel coordinate of the down event.
         * @type {import("ol/pixel").Pixel}
         */
    this.downPixel_ = null;

    /**
         * Longpress event succed.
         */
    this.succed_ = false;

    /**
         * Number of finger on the map.
         */
    this.fingerNb_ = 0;
  }

  handleEvent(event) {
    super.handleEvent(event);
    return true;
  }

  /**
     * On down event, start the timer for long press event.
     * @param {import('ol').MapBrowserEvent} event The map event.
     */
  handleDownEvent(event) {
    this.fingerNb_++;
    if (this.fingerNb_ === 1) {
      this.downPixel_ = event.pixel;
      this.succed_ = false;
      this.dispatchEvent(new LongPressEvent(LongPressEventType.LONGPRESS_START, event.coordinate));
      this.timeout_ = setTimeout(() => this.succed_ = true, this.duration_);
    } else {
      this.cancelLongPress_(event, true);
    }
    return true;
  }

  /**
     * On drag event, cancel the timer for long press event.
     * @param {import('ol').MapBrowserEvent} event The map event.
     */
  handleDragEvent(event) {
    if (this.fingerNb_ === 1) {
      this.cancelLongPress_(event);
    }
    return true;
  }

  /**
     * On move event, cancel the timer for long press event.
     * @param {import('ol').MapBrowserEvent} event The map event.
     */
  handleMoveEvent(event) {
    if (this.fingerNb_ === 1) {
      this.cancelLongPress_(event);
    }
    return true;
  }

  /**
     * On up event, cancel the timer for long press event.
     * @param {import('ol').MapBrowserEvent} event The map event.
     */
  handleUpEvent(event) {
    this.fingerNb_--;
    if (this.fingerNb_ === 0) {
      if (this.succed_) {
        this.dispatchEvent(new LongPressEvent(LongPressEventType.LONGPRESS_DONE));
      } else {
        this.cancelLongPress_(event, true);
      }
    }
    return true;
  }

  /**
     * Return true to forward events to other interactor.
     * @param {boolean} handled The map event.
     * @return {boolean} true
     */
  // eslint-disable-next-line no-unused-vars
  stopDown(handled) {
    return true;
  }

  /**
     * Cancel the timer for long press event.
     * @private
     * @param {import('ol').MapBrowserEvent} event The map event.
     * @param {boolean} force If true, force to cancel timer.
     */
  cancelLongPress_(event, force) {
    if (force || (this.downPixel_ && this.timeout_
            && distance(event.pixel, this.downPixel_) > this.tolerence_)) {
      clearTimeout(this.timeout_);
      this.dispatchEvent(new LongPressEvent(LongPressEventType.LONGPRESS_CANCEL));
      this.downPixel_ = undefined;
      this.succed_ = false;
    }
  }
}
